import { Component, Context, createContext, h, useContext, useEffect, useRef } from "@lukekaalim/act";

import { Observable, Subject } from 'rxjs';

export type BeatCallback = (
  now: DOMHighResTimeStamp,
  delta: DOMHighResTimeStamp,
  frame: number,
) => void

export type BeatDefinition = {
  context: Context<BeatController>,

  useBeat(subscriber?: BeatCallback): BeatController["subscribe"],
  useTick(): BeatController["tick"],
  useObservable(): Observable<[
    now: DOMHighResTimeStamp,
    delta: DOMHighResTimeStamp,
    frame: number,
  ]>,
  Provider: Component<{ controller?: BeatController }>,
  createController(): BeatController,
}

export type BeatController = {
  subscribe(subscriber: BeatCallback): () => void;
  tick(now: DOMHighResTimeStamp): void;

  beat: Observable<[
    now: DOMHighResTimeStamp,
    delta: DOMHighResTimeStamp,
    frame: number,
  ]>,
}

export const useBeat = (beat: BeatDefinition, subscriber?: BeatCallback) => {
  return beat.useBeat(subscriber);
};

const createBeatController = (): BeatController => {
  const beat = new Subject<[
    now: DOMHighResTimeStamp,
    delta: DOMHighResTimeStamp,
    frame: number,
  ]>()

  let lastTick = 0;
  let frame = 0;

  const subscribe = (subscriber: BeatCallback): () => void => {
    const subscription = beat.subscribe(([now, delta, frame]) => subscriber(now, delta, frame));

    return () => void subscription.unsubscribe();
  }

  const tick = (now: DOMHighResTimeStamp) => {
    const delta = lastTick && now - lastTick;
    lastTick = now;
    const currentFrame = frame++;
    try {
      beat.next([now, delta, currentFrame])
    } catch (error) {
      console.warn(error)
    }
  }

  return { subscribe, tick, beat };
}

export const createBeat = (): BeatDefinition => {
  const useBeat = (subscriber?: BeatCallback) => {
    const controller = useContext(context);

    useEffect(() => {
      if (subscriber)
        return controller.subscribe(subscriber)

    }, [controller, subscriber])

    return controller.subscribe;
  };
  const useTick = () => {
    const controller = useContext(context);
    return controller.tick;
  }
  const useObservable = () => {
    const controller = useContext(context);
    return controller.beat;
  }

  const context = createContext(createBeatController());

  return {
    useBeat,
    useTick,
    context,
    useObservable,
    Provider({ controller, children }) {
      const localController = useRef(createBeatController()).get();

      return h(context.Provider, { value: controller || localController }, children);
    },
    createController: createBeatController,
  }
}